import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TablePagination,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Person,
  Business,
  Receipt,
  Home,
  AttachMoney,
  Warning,
  TrendingDown,
  Percent,
  Search,
  Info,
  Pending,
  Done,
  RateReview
} from '@mui/icons-material';

const RateConcessionApproval = () => {
  const canWriteConcession = hasWritePermission('plot-booking.rate-concession-approval');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  // Sample rate concession approval data
  const [concessionData] = useState([
    {
      id: 1,
      bookingId: 'BK001',
      plotNo: 'P101',
      plotAmount: '₹54,00,000',
      plotRate: '₹4,500/sq ft',
      requestAmount: '₹48,00,000',
      differenceAmount: '₹6,00,000',
      customerDetails: {
        name: 'John Smith',
        id: 'CUST001',
        mobile: '9876543210',
        email: 'john.smith@email.com',
      },
      associateDetails: {
        name: 'Sarah Wilson',
        id: 'ASSOC001',
        mobile: '9876543220',
        department: 'Sales',
      },
      requestDate: '2025-10-01',
      status: 'Pending',
      plotSize: '30x40 ft',
      plotArea: '1200 sq ft',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      currentRate: '4500',
      requestedRate: '4000',
      concessionPercentage: '11.11%',
      reason: 'Market Rate Adjustment',
      justification: 'Customer is a loyal client with multiple bookings. Market rates have decreased in the area.',
    },
    {
      id: 2,
      bookingId: 'BK002',
      plotNo: 'P205',
      plotAmount: '₹81,90,000',
      plotRate: '₹5,200/sq ft',
      requestAmount: '₹75,00,000',
      differenceAmount: '₹6,90,000',
      customerDetails: {
        name: 'Sarah Johnson',
        id: 'CUST002',
        mobile: '9876543211',
        email: 'sarah.johnson@email.com',
      },
      associateDetails: {
        name: 'Mike Davis',
        id: 'ASSOC002',
        mobile: '9876543221',
        department: 'Sales',
      },
      requestDate: '2025-10-03',
      status: 'Approved',
      plotSize: '35x45 ft',
      plotArea: '1575 sq ft',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      currentRate: '5200',
      requestedRate: '4762',
      concessionPercentage: '8.42%',
      reason: 'Bulk Booking Discount',
      justification: 'Customer has booked multiple plots and qualifies for bulk discount.',
    },
    {
      id: 3,
      bookingId: 'BK003',
      plotNo: 'P321',
      plotAmount: '₹1,20,00,000',
      plotRate: '₹6,000/sq ft',
      requestAmount: '₹1,10,00,000',
      differenceAmount: '₹10,00,000',
      customerDetails: {
        name: 'Michael Brown',
        id: 'CUST003',
        mobile: '9876543212',
        email: 'michael.brown@email.com',
      },
      associateDetails: {
        name: 'Lisa Chen',
        id: 'ASSOC003',
        mobile: '9876543222',
        department: 'Sales',
      },
      requestDate: '2025-10-05',
      status: 'Rejected',
      plotSize: '40x50 ft',
      plotArea: '2000 sq ft',
      siteName: 'Paradise Heights',
      sectorName: 'Sector C',
      blockName: 'Block 3',
      currentRate: '6000',
      requestedRate: '5500',
      concessionPercentage: '8.33%',
      reason: 'Competitive Pricing',
      justification: 'Request exceeds maximum allowable concession percentage.',
    },
    {
      id: 4,
      bookingId: 'BK004',
      plotNo: 'P456',
      plotAmount: '₹1,85,62,500',
      plotRate: '₹7,500/sq ft',
      requestAmount: '₹1,70,00,000',
      differenceAmount: '₹15,62,500',
      customerDetails: {
        name: 'Emily Davis',
        id: 'CUST004',
        mobile: '9876543213',
        email: 'emily.davis@email.com',
      },
      associateDetails: {
        name: 'Robert Kim',
        id: 'ASSOC004',
        mobile: '9876543223',
        department: 'Sales',
      },
      requestDate: '2025-10-07',
      status: 'Under Review',
      plotSize: '45x55 ft',
      plotArea: '2475 sq ft',
      siteName: 'Royal Gardens',
      sectorName: 'Sector D',
      blockName: 'Block 4',
      currentRate: '7500',
      requestedRate: '6869',
      concessionPercentage: '8.41%',
      reason: 'Customer Loyalty Discount',
      justification: 'Long-term customer with excellent payment history requesting reasonable discount.',
    },
    {
      id: 5,
      bookingId: 'BK005',
      plotNo: 'P789',
      plotAmount: '₹73,72,800',
      plotRate: '₹4,800/sq ft',
      requestAmount: '₹70,00,000',
      differenceAmount: '₹3,72,800',
      customerDetails: {
        name: 'David Wilson',
        id: 'CUST005',
        mobile: '9876543214',
        email: 'david.wilson@email.com',
      },
      associateDetails: {
        name: 'Anna Garcia',
        id: 'ASSOC005',
        mobile: '9876543224',
        department: 'Sales',
      },
      requestDate: '2025-10-08',
      status: 'Pending',
      plotSize: '32x48 ft',
      plotArea: '1536 sq ft',
      siteName: 'Green Valley',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      currentRate: '4800',
      requestedRate: '4557',
      concessionPercentage: '5.06%',
      reason: 'Early Bird Offer',
      justification: 'Customer made advance payment and qualifies for early bird discount.',
    },
    {
      id: 6,
      bookingId: 'BK006',
      plotNo: 'P654',
      plotAmount: '₹1,02,75,200',
      plotRate: '₹5,200/sq ft',
      requestAmount: '₹95,00,000',
      differenceAmount: '₹7,75,200',
      customerDetails: {
        name: 'Lisa Anderson',
        id: 'CUST006',
        mobile: '9876543215',
        email: 'lisa.anderson@email.com',
      },
      associateDetails: {
        name: 'James Taylor',
        id: 'ASSOC006',
        mobile: '9876543225',
        department: 'Sales',
      },
      requestDate: '2025-10-09',
      status: 'Pending',
      plotSize: '38x52 ft',
      plotArea: '1976 sq ft',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      currentRate: '5200',
      requestedRate: '4807',
      concessionPercentage: '7.55%',
      reason: 'Special Circumstances',
      justification: 'Customer facing temporary financial constraints due to business impact.',
    },
  ]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setViewDialogOpen(true);
  };

  const handleApproveRequest = (requestId) => {
    setApproveLoading(true);
    // Simulate API call
    setTimeout(() => {
      setSuccessMessage(`Rate concession request for Booking ${concessionData.find(r => r.id === requestId)?.bookingId} has been approved.`);
      setApproveLoading(false);
      console.log('Approving request:', requestId);
    }, 1000);
  };

  const handleRejectRequest = (requestId) => {
    setRejectLoading(true);
    // Simulate API call
    setTimeout(() => {
      setSuccessMessage(`Rate concession request for Booking ${concessionData.find(r => r.id === requestId)?.bookingId} has been rejected.`);
      setRejectLoading(false);
      console.log('Rejecting request:', requestId);
    }, 1000);
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const getFilteredConcessionData = () => {
    let filteredData = concessionData;

    // Filter by table search query
    if (tableSearchQuery.trim()) {
      filteredData = filteredData.filter(request =>
        Object.values(request).some(value => {
          if (typeof value === 'object' && value !== null) {
            return Object.values(value).some(nestedValue =>
              String(nestedValue).toLowerCase().includes(tableSearchQuery.toLowerCase())
            );
          }
          return String(value).toLowerCase().includes(tableSearchQuery.toLowerCase());
        })
      );
    }

    // Filter by tab selection
    if (tabValue === 1) {
      filteredData = filteredData.filter(request => request.status === 'Pending');
    } else if (tabValue === 2) {
      filteredData = filteredData.filter(request => request.status === 'Under Review');
    } else if (tabValue === 3) {
      filteredData = filteredData.filter(request => request.status === 'Approved');
    } else if (tabValue === 4) {
      filteredData = filteredData.filter(request => request.status === 'Rejected');
    }

    return filteredData;
  };

  const getFilteredByStatus = (status) => {
    return concessionData.filter(request => request.status === status);
  };

  const getSearchSummary = () => {
    const filteredData = getFilteredConcessionData();
    return `${filteredData.length} request(s) found`;
  };

  // Summary calculations
  const totalRequests = concessionData.length;
  const pendingCount = getFilteredByStatus('Pending').length;
  const underReviewCount = getFilteredByStatus('Under Review').length;
  const approvedCount = getFilteredByStatus('Approved').length;
  const rejectedCount = getFilteredByStatus('Rejected').length;

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Pending':
        return {
          backgroundColor: '#ff9800',
          color: 'white',
        };
      case 'Under Review':
        return {
          backgroundColor: '#2196f3',
          color: 'white',
        };
      case 'Approved':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      case 'Rejected':
        return {
          backgroundColor: '#f44336',
          color: 'white',
        };
      default:
        return {};
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <RateReview sx={{ color: '#1976d2', fontSize: 32 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
          Rate Concession Approval Management
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert
          severity="success"
          sx={{
            mb: 4,
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle sx={{ fontSize: 20 }} />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Receipt sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Requests
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {totalRequests}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#fff3e0', 
            border: '1px solid #ffcc02',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Pending sx={{ color: '#f57c00', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Pending Requests
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {pendingCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e1f5fe', 
            border: '1px solid #81d4fa',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Info sx={{ color: '#0288d1', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Under Review
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0288d1', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {underReviewCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Done sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Approved Requests
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {approvedCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#ffebee', 
            border: '1px solid #ffcdd2',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Cancel sx={{ color: '#d32f2f', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Rejected Requests
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {rejectedCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid> */}

      {/* Main Content Card */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <RateReview sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Rate Concession Requests for Approval
              </Typography>
            </Box>
            <TextField
              label="Search Requests..."
              variant="outlined"
              size="small"
              value={tableSearchQuery}
              onChange={(e) => setTableSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          </Box>

          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ fontSize: 20 }} />
              {getSearchSummary()}
            </Box>
          </Alert>

          {/* Tab Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="request status tabs">
              <Tab label="All Requests" />
              <Tab label="Pending" />
              <Tab label="Under Review" />
              <Tab label="Approved" />
              <Tab label="Rejected" />
            </Tabs>
          </Box>

          {getFilteredConcessionData().length > 0 ? (
            <TableContainer sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Booking Info</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Plot Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Financial Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Customer Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Associate Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Request Info</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredConcessionData()
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((request, index) => (
                      <TableRow key={request.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{page * rowsPerPage + index + 1}</TableCell>

                        {/* Booking Info */}
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Receipt sx={{ color: '#666', fontSize: 16 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {request.bookingId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                📅 {request.requestDate}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Plot Details */}
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Home sx={{ color: '#666', fontSize: 16 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                Plot #{request.plotNo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                📏 {request.plotSize}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary">
                                📐 {request.plotArea}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Financial Details */}
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney sx={{ color: '#666', fontSize: 16 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                {request.plotAmount}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Rate: {request.plotRate}
                              </Typography>
                              <br />
                              <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600 }}>
                                Req: {request.requestAmount}
                              </Typography>
                              <br />
                              <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                                Diff: {request.differenceAmount}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        {/* Customer Details */}
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Person sx={{ color: '#666', fontSize: 16 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                {request.customerDetails.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {request.customerDetails.id}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary">
                                📱 {request.customerDetails.mobile}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Associate Details */}
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Business sx={{ color: '#666', fontSize: 16 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                {request.associateDetails.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {request.associateDetails.id}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary">
                                🏢 {request.associateDetails.department}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Request Info */}
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TrendingDown sx={{ color: '#666', fontSize: 16 }} />
                            <Box>
                              <Chip
                                label={request.status}
                                sx={{
                                  ...getStatusStyle(request.status),
                                  fontWeight: 600,
                                  fontSize: '12px'
                                }}
                                size="small"
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        {/* Actions */}
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleViewRequest(request)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>

                            {request.status === 'Pending' && (
                              <>
                                <Tooltip title="Approve Request">
                                  <IconButton
                                    color="success"
                                    size="small"
                                    onClick={() => handleApproveRequest(request.id)}
                                    disabled={approveLoading}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject Request">
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => handleRejectRequest(request.id)}
                                    disabled={rejectLoading}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <TablePagination
                
                component="div"
                count={getFilteredConcessionData().length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          ) : (
            <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Warning sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    No Rate Concession Requests Found
                  </Typography>
                  <Typography variant="body2">
                    No rate concession requests available for approval.
                  </Typography>
                </Box>
              </Box>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* View Request Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <RateReview sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
              Rate Concession Request Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              {/* Booking & Plot Information */}
              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Booking & Plot Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Booking ID:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedRequest.bookingId}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Plot No:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedRequest.plotNo}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Site Location:</Typography>
                    <Typography variant="body1">
                      {selectedRequest.siteName}, {selectedRequest.sectorName}, {selectedRequest.blockName}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Plot Size:</Typography>
                    <Typography variant="body1">{selectedRequest.plotSize}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Plot Area:</Typography>
                    <Typography variant="body1">{selectedRequest.plotArea}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Chip
                      label={selectedRequest.status}
                      sx={getStatusStyle(selectedRequest.status)}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Rate & Amount Details */}
              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom color="secondary">
                  Rate & Amount Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Current Plot Rate:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                      {selectedRequest.plotRate}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Requested Rate:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                      ₹{selectedRequest.requestedRate}/sq ft
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Current Plot Amount:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {selectedRequest.plotAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Requested Amount:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                      {selectedRequest.requestAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Difference Amount:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      {selectedRequest.differenceAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Concession Percentage:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      {selectedRequest.concessionPercentage}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Customer & Associate Information */}
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person />
                      Customer Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Name:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {selectedRequest.customerDetails.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Customer ID:</Typography>
                        <Typography variant="body1">{selectedRequest.customerDetails.id}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Mobile:</Typography>
                        <Typography variant="body1">{selectedRequest.customerDetails.mobile}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Email:</Typography>
                        <Typography variant="body1">{selectedRequest.customerDetails.email}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business />
                      Associate Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Name:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {selectedRequest.associateDetails.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Associate ID:</Typography>
                        <Typography variant="body1">{selectedRequest.associateDetails.id}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Mobile:</Typography>
                        <Typography variant="body1">{selectedRequest.associateDetails.mobile}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Department:</Typography>
                        <Typography variant="body1">{selectedRequest.associateDetails.department}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>

              {/* Request Information */}
              <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom color="warning.main">
                  Request Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Request Date:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedRequest.requestDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Reason:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedRequest.reason}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Justification:</Typography>
                    <Typography variant="body1">
                      {selectedRequest.justification}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRequest?.status === 'Pending' && (
            <>
              <Button
                onClick={() => {
                  handleApproveRequest(selectedRequest.id);
                  setViewDialogOpen(false);
                }}
                color="success"
                variant="contained"
                startIcon={<CheckCircle />}
              >
                Approve
              </Button>
              <Button
                onClick={() => {
                  handleRejectRequest(selectedRequest.id);
                  setViewDialogOpen(false);
                }}
                color="error"
                variant="contained"
                startIcon={<Cancel />}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={() => setViewDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RateConcessionApproval;
